import PropTypes from 'prop-types';
import { getImage } from 'gatsby-plugin-image';
import { BLOCKS } from '@contentful/rich-text-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { navigate } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { Link, useIntl } from 'gatsby-plugin-intl';
import parse from 'html-react-parser';

import { getLunrIndex, lunrSearch, SEARCH_CATEGORIES } from '../../utils/searchUtils';
import SectionContainer from '../SectionContainer';
import RichText from '../RichTextV2';
import themeLib from '../../themes/phoenix/theme';

import {
  BodyCopy, Heading, Image, Search, SearchResultsWrapper, Section, StyledPredictiveSearchContainer,
} from './styles';

const HeroBannerOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

/**
 * Section that displays the hero banner for the page. Uses the HeroBanner content type
 * @param {Object} data - contentful data obj.
 * @param {String} data.sectionId - id of the section for deep linking
 * @param {String} data.backgroundColor - background color to be used for the component
 * @param {{raw: string}} data.heroTitle - Contentful richtext field for the Hero banner title
 * @param {{raw: string}} data.heroText - Contentful richtext field for the Hero banner body copy
 * @param {Object} data.heroLinks - Button /  LinkWrapper Content type reference for the hero CallToAction
 * @param {Object} data.heroImages - DamAsset Content type reference for the hero banner image
 */

const FAQHeroBanner = ({
  data,
  size,
  searchSection,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const intl = useIntl();

  const results = useMemo(() => {
    const lunr = getLunrIndex(intl.locale);
    return lunrSearch({ lunr, query: searchValue })
      .filter(result => result.category === SEARCH_CATEGORIES.FAQ);
  }, [searchValue, intl.locale]);

  const focusOutsideOfSearch = useCallback((event) => {
    const { keyCode } = event;
    if (keyCode === 9) {
      if (event.target.classList.contains('faq-search-class')) {
        setSearchOpen(true);
        return;
      }
      setSearchOpen(false);
    }
  }, []);

  const clickOutsideOfSearch = useCallback((event) => {
    if (event.target.classList.contains('faq-search-class')) {
      setSearchOpen(true);
      return;
    }
    setSearchOpen(false);
  }, []);

  useEffect(() => {
    if (window) {
      window.addEventListener('keyup', focusOutsideOfSearch);
      window.addEventListener('click', clickOutsideOfSearch);
    }
    return () => {
      window.removeEventListener('keyup', focusOutsideOfSearch);
      window.removeEventListener('click', clickOutsideOfSearch);
    };
  }, [focusOutsideOfSearch, clickOutsideOfSearch]);

  if (!data) return null;
  const {
    sectionId,
    backgroundColor,
    heroTitle,
    heroImage,
  } = data;
  const image = getImage(heroImage?.asset?.localImage);

  const onSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setSearchOpen(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      navigate(`/${intl.locale}/search-results?filter=2&searchQuery=${encodeURIComponent(searchValue)}`);
    }
  };

  return (
    <Section id={sectionId} background={backgroundColor} theme={themeLib}>
      <SectionContainer centered fluid>
        <RichText document={heroTitle} options={HeroBannerOptions} />
        {searchSection
          && (
            <>
              <Search
                id="faq-search"
                value={searchValue}
                onChange={onSearchValueChange}
                onKeyDown={handleKeyDown}
                type="search"
                placeholder={renderRichText(searchSection?.headingRTF)?.[0]?.props?.children?.[0]}
                aria-live="polite"
                autoComplete="on"
                className="faq-search-class"
              />

              {searchOpen && (
                <SearchResultsWrapper>
                  <StyledPredictiveSearchContainer
                    className={[
                      'no-bullets',
                      'searchable-marker',
                    ].join(' ')}
                  >
                    {results
                      .map(({ page, url }) => (
                        <li key={url}>
                          <Link
                            className="searchable-marker"
                            to={url}
                            state={{ fromFeed: true }}
                            onClick={() => {
                              setSearchOpen(false);
                              setSearchValue('');
                            }}
                          >
                            {parse(page)}
                          </Link>
                        </li>
                      ))
                      .filter((_, index) => index <= 4)}
                  </StyledPredictiveSearchContainer>
                </SearchResultsWrapper>
              )}
            </>
          )}
        {image && (
        <Image
          size={size}
          image={image}
          alt={heroImage?.altText || ''}
          objectFit="contain"
        />
        )}
      </SectionContainer>
    </Section>
  );
};

FAQHeroBanner.propTypes = {
  data: PropTypes.shape().isRequired,
  size: PropTypes.string,
  searchSection: PropTypes.shape(),
};

FAQHeroBanner.defaultProps = {
  size: null,
  searchSection: null,
};
export default FAQHeroBanner;
